/**
 * Consent state for the one thing on this site that needs it: sending
 * anonymous usage events to a third-party analytics destination (GA4).
 * Nothing else on the estimator (the calculation engine, the lead form,
 * the email-my-estimate feature) reads or depends on this - a homeowner
 * who declines analytics still gets the full estimator experience.
 *
 * PIPEDA-conscious default: analytics is OFF (`"undecided"`, treated as
 * not-consented everywhere in this package) until a visitor makes an
 * explicit choice. This is deliberately more conservative than PIPEDA
 * strictly requires for anonymous, non-identifying usage analytics
 * (implied consent can be defensible for that) - opt-in is the safer,
 * unambiguous choice, and it costs nothing here since no event carries
 * personal information anyway (see pii.ts). See docs/privacy-security.md
 * for the fuller reasoning and what this is/isn't a substitute for.
 */
export type ConsentChoice = "granted" | "declined" | "undecided";

export interface ConsentState {
  analytics: ConsentChoice;
  /** ISO 8601 timestamp of when the choice was made; undefined while "undecided". */
  decidedAt?: string;
}

export const CONSENT_STORAGE_KEY = "reno-estimator:consent";

export const UNDECIDED_CONSENT: ConsentState = { analytics: "undecided" };

function hasLocalStorage(): boolean {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch {
    // Some browsers throw just accessing localStorage under certain
    // privacy settings (e.g. fully blocked third-party storage) - treat
    // that the same as "not available" rather than letting it propagate.
    return false;
  }
}

/**
 * Reads the visitor's stored consent choice. Never throws; any read/parse
 * failure (corrupted value, storage unavailable, private browsing
 * quirks) is treated as "undecided" - the safe default - rather than
 * assumed granted.
 */
export function getStoredConsent(): ConsentState {
  if (!hasLocalStorage()) return UNDECIDED_CONSENT;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return UNDECIDED_CONSENT;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (parsed.analytics === "granted" || parsed.analytics === "declined") {
      return { analytics: parsed.analytics, decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : undefined };
    }
    return UNDECIDED_CONSENT;
  } catch {
    return UNDECIDED_CONSENT;
  }
}

/** Persists an explicit consent choice. A no-op (never throws) when storage isn't available. */
export function setStoredConsent(choice: Exclude<ConsentChoice, "undecided">, now: () => Date = () => new Date()): ConsentState {
  const state: ConsentState = { analytics: choice, decidedAt: now().toISOString() };
  if (hasLocalStorage()) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage full/blocked - the in-memory choice for this page view
      // still applies via the caller's own state; nothing further to do.
    }
  }
  return state;
}

/** Clears a stored choice, returning the visitor to "undecided" (e.g. a "reset privacy choices" control). */
export function clearStoredConsent(): void {
  if (!hasLocalStorage()) return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Nothing to do if storage is unavailable.
  }
}

export function hasAnalyticsConsent(state: ConsentState): boolean {
  return state.analytics === "granted";
}
